import React, { useEffect, useState } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";

import FirstSlide from "./components/FirstSlide";
import HeroArt from "./components/HeroArt";
import HeroVisual from "./components/HeroVisual";

const API = process.env.REACT_APP_API_URL;

// A section header with its own "see more" link, so the landing page is a
// doorway into the site rather than a dead end.
const SectionHead = ({ title, note, to, more }) => (
  <Stack
    direction="row"
    sx={{ alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 2, marginBottom: 3 }}
  >
    <Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: "1.4rem", md: "1.7rem" },
          letterSpacing: "-0.01em",
          background: "linear-gradient(135deg,#0F172A 0%,#2563EB 70%,#7C3AED 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {title}
      </Typography>
      {note && <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>{note}</Typography>}
    </Box>
    <Button component={Link} to={to} variant="outlined"
      sx={{ borderColor: "rgba(15,23,42,.14)", color: "text.primary", background: "#fff" }}>
      {more}
    </Button>
  </Stack>
);

const Card = ({ children, sx }) => (
  <Box
    sx={{
      background: "#fff",
      border: "1px solid rgba(15,23,42,.08)",
      borderRadius: "18px",
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 10px 26px rgba(15,23,42,.05)",
      transition: "transform .18s ease, box-shadow .18s ease",
      "&:hover": { transform: "translateY(-3px)", boxShadow: "0 18px 38px rgba(37,99,235,.13)" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Home = () => {
  const { t } = useTranslation();
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [about, setAbout] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);

    // previews only — a few rows each, so the landing page stays light
    axios.get(`${API}/news`).then((r) => setNews((r?.data?.data ?? []).slice(0, 3))).catch(() => { });
    axios.get(`${API}/photo-gallery`).then((r) => setGallery((r?.data?.data ?? []).slice(0, 4))).catch(() => { });
    axios.get(`${API}/about_us`).then((r) => {
      const text = r?.data?.data?.description ?? r?.data?.data?.[0]?.description ?? "";
      setAbout(String(text).replace(/<[^>]+>/g, "").slice(0, 260));
    }).catch(() => { });
  }, []);

  return (
    <Box>
      {/* ---- hero: one dark immersive band; everything below stays light ---- */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          // pulled up under the fixed header so the band starts at the very top
          marginTop: "-72px",
          paddingTop: "72px",
          background: "linear-gradient(150deg,#0B1220 0%,#101C3D 45%,#1B1250 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(760px 420px at 72% 18%, rgba(56,189,248,.20), transparent 62%)," +
              "radial-gradient(680px 420px at 18% 82%, rgba(168,85,247,.18), transparent 60%)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            opacity: 0.35,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(circle at 50% 35%, #000 40%, transparent 78%)",
            WebkitMaskImage: "radial-gradient(circle at 50% 35%, #000 40%, transparent 78%)",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            sx={{
              alignItems: "center",
              gap: { xs: 5, md: 3 },
              padding: { xs: "44px 0 64px", md: "70px 0 92px" },
            }}
          >
            <Box sx={{ flex: "1 1 520px", width: "100%" }}>
              <FirstSlide />
            </Box>
            <Box sx={{ flex: "1 1 520px", display: "flex", justifyContent: "center", width: "100%" }}>
              <HeroVisual />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ---- what the platform gives you ---- */}
      <Box sx={{ background: "#fff", borderBlock: "1px solid rgba(15,23,42,.07)", padding: { xs: "48px 0", md: "64px 0" } }}>
        <Container maxWidth="lg">
          <SectionHead
            title="ابدأ من امتحاناتك"
            note="امتحاناتك المتاحة ونتائجك السابقة في مكان واحد"
            to="/exams"
            more={t("exams")}
          />
          <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
            {[
              { icon: "📝", title: t("exams"), note: "ادخل امتحاناتك المتاحة الآن", to: "/exams" },
              { icon: "✅", title: t("completed_exams"), note: "راجع إجاباتك وعلامتك", to: "/completed-exams" },
              { icon: "👤", title: t("profile"), note: "لوحة أدائك وشاراتك", to: "/profile" },
            ].map((item) => (
              <Card key={item.title} sx={{ flex: "1 1 240px", padding: "20px" }}>
                <Link to={item.to}>
                  <Box sx={{ fontSize: "1.7rem", marginBottom: "6px" }}>{item.icon}</Box>
                  <Typography sx={{ fontWeight: 800, color: "text.primary" }}>{item.title}</Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.85rem", marginTop: "2px" }}>
                    {item.note}
                  </Typography>
                </Link>
              </Card>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ---- about ---- */}
      {about && (
        <Container maxWidth="lg" sx={{ padding: { xs: "48px 0", md: "64px 0" } }}>
          <SectionHead title={t("about_us")} to="/about-us" more={t("more") || "المزيد"} />
          <Card sx={{ padding: { xs: "22px", md: "30px" } }}>
            <Typography sx={{ color: "text.secondary", lineHeight: 2.1 }}>{about}…</Typography>
          </Card>
        </Container>
      )}

      {/* ---- news ---- */}
      {news.length > 0 && (
        <Box sx={{ background: "#fff", borderBlock: "1px solid rgba(15,23,42,.07)", padding: { xs: "48px 0", md: "64px 0" } }}>
          <Container maxWidth="lg">
            <SectionHead title={t("news")} note="آخر ما نُشر على المنصة" to="/news" more={t("more") || "المزيد"} />
            <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
              {news.map((item) => (
                <Card key={item.id} sx={{ flex: "1 1 280px", maxWidth: "100%" }}>
                  <Link to={`/news/${item.id}`}>
                    {item.image && (
                      <Box component="img" src={item.image} alt="" loading="lazy"
                        sx={{ width: "100%", height: "165px", objectFit: "cover", display: "block" }} />
                    )}
                    <Box sx={{ padding: "16px" }}>
                      <Typography className="text-ellipsis-2" sx={{ fontWeight: 800, color: "text.primary" }}>
                        {item.title}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", fontSize: "0.78rem", marginTop: "6px" }}>
                        {String(item.created_at || "").slice(0, 10)}
                      </Typography>
                    </Box>
                  </Link>
                </Card>
              ))}
            </Stack>
          </Container>
        </Box>
      )}

      {/* ---- gallery ---- */}
      {gallery.length > 0 && (
        <Container maxWidth="lg" sx={{ padding: { xs: "48px 0", md: "64px 0" } }}>
          <SectionHead title={t("album")} note="لقطات من المنصة" to="/album" more={t("more") || "المزيد"} />
          <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
            {gallery.map((item) => (
              <Card key={item.id} sx={{ flex: "1 1 220px" }}>
                <Box component="img" src={item.image} alt="" loading="lazy"
                  sx={{ width: "100%", height: "150px", objectFit: "cover", display: "block" }} />
              </Card>
            ))}
          </Stack>
        </Container>
      )}

      {/* ---- contact ---- */}
      <Container maxWidth="lg" sx={{ paddingBottom: { xs: "56px", md: "80px" } }}>
        <Box
          sx={{
            borderRadius: "24px",
            padding: { xs: "28px 22px", md: "40px" },
            background: "linear-gradient(135deg,#0F172A 0%,#1E3A8A 55%,#4C1D95 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.25rem", md: "1.5rem" } }}>
              عندك سؤال أو تحتاج مساعدة؟
            </Typography>
            <Typography sx={{ opacity: 0.85, marginTop: "4px" }}>
              فريق المنصة جاهز للرد عليك.
            </Typography>
          </Box>
          <Button component={Link} to="/contact-us" variant="contained"
            sx={{ background: "#fff", color: "#0F172A", paddingInline: "28px", paddingBlock: "12px",
                  "&:hover": { background: "#F1F5F9" } }}>
            {t("contact_us")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Home;
